import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import BankPaymentsTable from "../../component/dashboard/BankAccountRecords";
import DownloadPaymentExcel from "../../component/dashboard/DownloadPaymentExcel";
import DashboardSkeleton from "./DashboardSkeleton";
import { fetchDashboardStats, fetchSmsBalance } from "../../api/dashboardApi";
import {
  FaArrowDown,
  FaArrowUp,
  FaUserFriends,
  FaBook,
  FaMoneyBillWave,
  FaLayerGroup,
  FaChartLine,
} from "react-icons/fa";
import StatContainer from "./StatContainer";

const DashBoard = () => {
  const [data, setData] = useState(null);
  const [smsBalanceData, setSmsBalanceData] = useState(null);
  const [isSmsBalanceLoading, setIsSmsBalanceLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [isLoading, setIsLoading] = useState(false);
  const downloadExcelRef = useRef(null);
  const hasShownLowSmsWarning = useRef(false);
  const navigate = useNavigate();

  const parseSmsBalanceValue = (balanceText) => {
    if (!balanceText) return null;
    const numericValue = Number(String(balanceText).replace(/[^\d.]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  useEffect(() => {
    const fetchDashBoard = async () => {
      try {
        setIsLoading(true);
        const result = await fetchDashboardStats(period);
        setData(result);
      } catch (error) {
        if (error && error.status === 401) {
          alert("Unauthorized action!");
          navigate("/");
        } else {
          console.error("Error fetching dashboard data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashBoard();
  }, [navigate, period]);

  useEffect(() => {
    const getSmsBalance = async () => {
      try {
        setIsSmsBalanceLoading(true);
        const result = await fetchSmsBalance();
        setSmsBalanceData(result?.data || null);
      } catch (error) {
        console.error("Error fetching SMS balance:", error);
      } finally {
        setIsSmsBalanceLoading(false);
      }
    };

    getSmsBalance();
  }, []);

  useEffect(() => {
    if (isSmsBalanceLoading || hasShownLowSmsWarning.current) {
      return;
    }

    const currentBalance = parseSmsBalanceValue(smsBalanceData?.remaining_balance);
    if (currentBalance !== null && currentBalance < 150) {
      toast.warn(`ATTENTION! SMS balance is low: ${smsBalanceData?.remaining_balance}`);
      hasShownLowSmsWarning.current = true;
    }
  }, [isSmsBalanceLoading, smsBalanceData]);

  // Function to render trend indicator with color
  const renderTrend = (value, suffix = "%") => {
    if (!value && value !== 0) return null;

    const isPositive = value >= 0;
    const absoluteValue = Math.abs(value);
    const color = isPositive ? "text-emerald-600" : "text-rose-600";
    const Arrow = isPositive ? FaArrowUp : FaArrowDown;

    return (
      <div className={`flex items-center ${color}`}>
        <Arrow size={12} className="mr-1" />
        <span className="text-sm font-medium">
          {absoluteValue.toFixed(2)}
          {suffix} {isPositive ? "increase" : "decrease"}
        </span>
      </div>
    );
  };

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  // Function to handle export report button click and scroll to bottom
  const handleExportReport = () => {
    // Scroll to the download excel button
    if (downloadExcelRef.current) {
      downloadExcelRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center' 
      });
    }
  };

  // Show skeleton while loading
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="w-full max-w-7xl mx-auto sm:p-4 md:p-8 bg-gray-50">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row items-start gap-6 md:items-center justify-between mb-8 border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Dashboard Overview
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor your key performance metrics
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <select
              className="bg-white border border-gray-300 rounded-lg py-2 px-4 pl-4 pr-10 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm text-gray-700 font-medium"
              value={period}
              onChange={handlePeriodChange}
            >
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="year">This year</option>
              <option value="all">All time</option>
            </select>
          </div>
          <button 
            onClick={handleExportReport}
            className="bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2 shadow-sm"
          >
            <FaChartLine size={14} />
            <span className="font-medium">Export Report</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {data && (
        <div className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Courses Count */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 transition-all duration-200 hover:shadow-lg flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wider">
                    Total Courses
                  </p>
                  <h2 className="text-3xl font-bold text-gray-800">
                    {data.course_count?.toLocaleString() || 0}
                  </h2>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg shadow-sm">
                  <FaBook className="text-blue-600" size={22} />
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Educational resources available
                </p>
              </div>
            </div>

            {/* User Count */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 transition-all duration-200 hover:shadow-lg flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wider">
                    Total Users
                  </p>
                  <h2 className="text-3xl font-bold text-gray-800">
                    {data.user_count?.toLocaleString() || 0}
                  </h2>
                </div>
                <div className="bg-indigo-100 p-3 rounded-lg shadow-sm">
                  <FaUserFriends className="text-indigo-600" size={22} />
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Registered platform members
                </p>
              </div>
            </div>

            {/* Lessons Count */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 transition-all duration-200 hover:shadow-lg flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wider">
                    Total Lessons
                  </p>
                  <h2 className="text-3xl font-bold text-gray-800">
                    {data.lesson_count?.toLocaleString() || 0}
                  </h2>
                </div>
                <div className="bg-amber-100 p-3 rounded-lg shadow-sm">
                  <FaLayerGroup className="text-amber-600" size={22} />
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Individual learning modules
                </p>
              </div>
            </div>

            {/* Revenue */}
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100 transition-all duration-200 hover:shadow-lg flex flex-col justify-between">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wider">
                    Total Revenue
                  </p>
                  <div className="flex items-baseline">
                    <span className="hidden font-bold text-gray-600 mr-1">
                      LKR
                    </span>
                    <h2
                      className={`font-bold text-gray-800 ${
                        (period === "all"
                          ? data.total_revenue
                          : data.period_revenue || 0
                        ).toLocaleString().length > 10
                          ? "text-xl"
                          : "text-3xl"
                      }`}
                    >
                      {(period === "all"
                        ? data.total_revenue || 0
                        : data.period_revenue || 0
                      ).toLocaleString()}
                    </h2>
                  </div>
                </div>
                <div className="bg-emerald-100 p-3 rounded-lg shadow-sm">
                  <FaMoneyBillWave className="text-emerald-600" size={22} />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                {data.revenue_change_percentage !== undefined ? (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">vs. previous period</p>
                    {renderTrend(data.revenue_change_percentage || 0)}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Total earnings to date
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white rounded-xl p-6 shadow-md border border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-1 uppercase tracking-wider">
                  SMS Balance   <a href="https://dashboard.smsapi.lk/" target="_blank" className="underline text-blue-600 text-xs">see more</a>
                </p>
                <h3
                  className={`text-2xl font-bold ${
                    isSmsBalanceLoading ? "text-gray-400 animate-pulse" : "text-gray-800"
                  }`}
                >
                  {isSmsBalanceLoading
                    ? "Loading..."
                    : smsBalanceData?.remaining_balance || "Unavailable"}
                </h3>
              </div>
              <div>
                <p className="text-sm text-gray-500">Expired on</p>
                <p className="text-sm font-medium text-gray-700">
                  {smsBalanceData?.expired_on || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Dashboard Content */}
      <div className=" hidden grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-md border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800">
              Revenue Overview
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">View:</span>
              <select className="bg-gray-100 border border-gray-200 rounded-md py-1 px-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                <option>Weekly</option>
                <option>Monthly</option>
              </select>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <p className="text-gray-500">
              Revenue chart will be displayed here
            </p>
          </div>
        </div>

        <div className="hidden bg-white rounded-xl p-6 shadow-md border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800">
              Recent Transactions
            </h3>
            <button className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {/* Placeholder for transactions */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-medium">JS</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">John Smith</p>
                  <p className="text-xs text-gray-500">
                    Course: Advanced React
                  </p>
                </div>
              </div>
              <p className="font-semibold">LKR 1,500</p>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-medium">AM</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Anna Miller</p>
                  <p className="text-xs text-gray-500">Course: UX Design</p>
                </div>
              </div>
              <p className="font-semibold">LKR 2,200</p>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <span className="text-green-600 font-medium">RP</span>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Robert Patel</p>
                  <p className="text-xs text-gray-500">Course: Python Basics</p>
                </div>
              </div>
              <p className="font-semibold">LKR 1,800</p>
            </div>
          </div>
        </div>
      </div>
      <StatContainer downloadExcelRef={downloadExcelRef} />
    </div>
  );
};

export default DashBoard;